import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

export interface BoothMediaState {
  localStream: MediaStream | null;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  permissionGranted: boolean;
  permissionDenied: boolean;
  isInitializing: boolean;
  error: string | null;
}

export const useBoothWebRTC = (boothId: string | undefined) => {
  const [state, setState] = useState<BoothMediaState>({
    localStream: null,
    isVideoEnabled: true,
    isAudioEnabled: true,
    permissionGranted: false,
    permissionDenied: false,
    isInitializing: false,
    error: null,
  });

  const streamRef = useRef<MediaStream | null>(null);
  const mountedRef = useRef(true);

  // Check if the current stream is still valid (tracks not ended)
  const isStreamValid = useCallback(() => {
    if (!streamRef.current) return false;
    const videoTrack = streamRef.current.getVideoTracks()[0];
    return videoTrack?.readyState === "live";
  }, []);

  // Request camera and mic permissions and create local stream
  const requestPermissions = useCallback(async (): Promise<boolean> => {
    // If we already have a valid stream, return true
    if (streamRef.current && isStreamValid()) {
      console.log("[BoothWebRTC] Stream already valid, reusing");
      return true;
    }

    // Clean up any dead stream first
    if (streamRef.current) {
      console.log("[BoothWebRTC] Cleaning up dead stream");
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setState(prev => ({ ...prev, isInitializing: true, error: null }));

    try {
      console.log("[BoothWebRTC] Requesting media permissions...");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user",
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Check if component is still mounted
      if (!mountedRef.current) {
        console.log("[BoothWebRTC] Component unmounted, stopping new stream");
        stream.getTracks().forEach(track => track.stop());
        return false;
      }

      console.log("[BoothWebRTC] Stream created:", {
        id: stream.id,
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length,
        videoReadyState: stream.getVideoTracks()[0]?.readyState,
      });

      streamRef.current = stream;
      
      setState({
        localStream: stream,
        isVideoEnabled: true,
        isAudioEnabled: true,
        permissionGranted: true,
        permissionDenied: false,
        isInitializing: false,
        error: null,
      });

      return true;
    } catch (error) {
      console.error("[BoothWebRTC] Permission denied:", error);
      
      if (!mountedRef.current) return false;

      const errorMessage = error instanceof Error 
        ? error.name === "NotAllowedError" 
          ? "Camera and microphone access denied. Please allow permissions to join."
          : error.message
        : "Failed to access camera/microphone";

      setState(prev => ({
        ...prev,
        localStream: null,
        permissionGranted: false,
        permissionDenied: true,
        isInitializing: false,
        error: errorMessage,
      }));

      return false;
    }
  }, [isStreamValid]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (!streamRef.current) return;
    
    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (videoTrack && videoTrack.readyState === "live") {
      videoTrack.enabled = !videoTrack.enabled;
      setState(prev => ({ ...prev, isVideoEnabled: videoTrack.enabled }));
    }
  }, []);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    if (!streamRef.current) return;
    
    const audioTrack = streamRef.current.getAudioTracks()[0];
    if (audioTrack && audioTrack.readyState === "live") {
      audioTrack.enabled = !audioTrack.enabled;
      setState(prev => ({ ...prev, isAudioEnabled: audioTrack.enabled }));
    }
  }, []);

  // Force mute (for moderation)
  const forceMute = useCallback(() => {
    if (!streamRef.current) return;
    const audioTrack = streamRef.current.getAudioTracks()[0];
    if (audioTrack && audioTrack.readyState === "live") {
      audioTrack.enabled = false;
      setState(prev => ({ ...prev, isAudioEnabled: false }));
    }
  }, []);

  // Force camera off (for moderation)
  const forceVideoOff = useCallback(() => {
    if (!streamRef.current) return;
    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (videoTrack && videoTrack.readyState === "live") {
      videoTrack.enabled = false;
      setState(prev => ({ ...prev, isVideoEnabled: false }));
    }
  }, []);

  // Stop all tracks and cleanup
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    
    setState({
      localStream: null,
      isVideoEnabled: true,
      isAudioEnabled: true,
      permissionGranted: false,
      permissionDenied: false,
      isInitializing: false,
      error: null,
    });
  }, []);

  // Track mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        console.log("[BoothWebRTC] Cleanup - stopping stream");
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Monitor track health - re-request if tracks die unexpectedly
  useEffect(() => {
    if (!state.localStream || !state.permissionGranted) return;

    const checkTrackHealth = () => {
      const videoTrack = state.localStream?.getVideoTracks()[0];
      if (videoTrack && videoTrack.readyState === "ended") {
        console.warn("[BoothWebRTC] Video track ended unexpectedly, clearing state");
        // Clear the dead stream from state so UI can show "camera off" and allow retry
        setState(prev => ({
          ...prev,
          localStream: null,
          permissionGranted: false,
          error: "Camera disconnected. Please try again.",
        }));
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
      }
    };

    // Check immediately and then periodically
    checkTrackHealth();
    const interval = setInterval(checkTrackHealth, 1000);
    return () => clearInterval(interval);
  }, [state.localStream, state.permissionGranted]);

  // Check if stream is truly active
  const isStreamActive = useCallback(() => {
    return isStreamValid();
  }, [isStreamValid]);

  return {
    ...state,
    requestPermissions,
    toggleVideo,
    toggleAudio,
    forceMute,
    forceVideoOff,
    stopStream,
    isStreamActive,
  };
};
